import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { Platform } from 'src/app/models/api';
import { PlatformIconPaths } from 'src/app/models/constants/platform.constants';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { ISelectOption, PlatformSelectOptions } from 'src/app/models/selectOptions';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { ISelectionButton } from '../../selection-button/selection-button.component';
import { RequiredFieldTitlePipe } from 'src/app/pipes/required-field-title/required-field-title.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';

@Component({
  selector: 'app-integration-platform-selection',
  templateUrl: './integration-platform-selection.component.html',
  styleUrls: ['./integration-platform-selection.component.scss'],
})
export class IntegrationPlatformSelectionComponent implements OnInit {
  @Input() public disabled: boolean;
  @Input() public platformFormControl: FormControl<string>;

  private readonly _requiredFieldPipe = inject(RequiredFieldTitlePipe);
  private readonly _labelsProviderPipe = inject(LabelsProviderPipe);
  private readonly _featureFlagsService = inject(FeatureFlagsService);

  public platformsListOptions: ISelectOption[];
  public platformSelectionButtons: BehaviorSubject<BehaviorSubject<ISelectionButton>[]> = new BehaviorSubject<
    BehaviorSubject<ISelectionButton>[]
  >([]);
  private _platformsForSelectionButtons: string[];
  private _platformIdToDispNameMap: { [id: string]: string | undefined } = {};

  ngOnInit(): void {
    const availablePlatforms = this._getAvailablePlatforms();

    this._platformsForSelectionButtons = this.disabled
      ? availablePlatforms.map((option) => option.id)
      : availablePlatforms.slice(0, 3).map((option) => option.id);
    availablePlatforms.forEach((option) => {
      this._platformIdToDispNameMap[option.value] = option.displayName;
    });

    this.platformsListOptions = availablePlatforms.filter(
      (option) => this._platformsForSelectionButtons.indexOf(<Platform>option.id) < 0
    );
    this.platformSelectionButtons.next(this._createPlatformSelectionButtons(this.platformFormControl.value));
  }

  public getPlatformTitle(): string {
    return this.disabled
      ? this._labelsProviderPipe.transform('integrationPage.editModePlatformField')
      : this._requiredFieldPipe.transform('integrationPage.addModePlatformField');
  }

  public onPlatformSelected(selectedButton: ISelectionButton) {
    this.platformFormControl.setValue(<Platform>selectedButton.id);
  }

  public onMorePlatformSelected(selectionOption: ISelectOption) {
    const selectedIndex = this._platformsForSelectionButtons.indexOf(this.platformFormControl.value);
    this._platformsForSelectionButtons[this._platformsForSelectionButtons.length - 1] = <Platform>selectionOption.id;

    const availablePlatforms = this._getAvailablePlatforms();

    this.platformsListOptions = availablePlatforms.filter(
      (option) => this._platformsForSelectionButtons.indexOf(<Platform>option.id) < 0
    );

    // if selected platform is going to be replaced with platform from drop down
    // publish platform change
    if (selectedIndex === this._platformsForSelectionButtons.length - 1) {
      this.platformFormControl.reset();
    }

    this.platformSelectionButtons.next(this._createPlatformSelectionButtons(this.platformFormControl.value));
  }

  private _createPlatformSelectionButtons(selectedId: string) {
    const platformSelectionButtons: BehaviorSubject<ISelectionButton>[] = [];
    const selectedIndex = this._platformsForSelectionButtons.indexOf(selectedId);

    this._platformsForSelectionButtons.forEach((platform, index) => {
      const isSelected = selectedIndex === index ? true : false;
      const displayName = this._platformIdToDispNameMap[platform];
      const description = displayName ? displayName : platform;

      if (!this.disabled || isSelected) {
        platformSelectionButtons.push(
          new BehaviorSubject<ISelectionButton>({
            id: platform,
            description: description,
            iconPath: PlatformIconPaths[platform],
            selected: isSelected,
          })
        );
      }
    });
    return platformSelectionButtons;
  }

  private _getAvailablePlatforms(): ISelectOption[] {
    return PlatformSelectOptions.filter((option) => {
      if (option.id === Platform.outpost_powerplatform) {
        return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.OutpostPowerPlatform);
      }
      return true;
    });
  }
}
